import type { ReactNode } from 'react'

import { useGetPost } from 'src/shared/api/feeds/use-get-post'
import { useMatchById } from 'src/shared/api/partner-match/use-match-by-id'
import { NotificationVerb } from 'src/shared/schemas/notifications'
import type { NotificationActivity, NotificationGroup } from 'src/shared/schemas/stream/notification'

import { NotificationMap } from '../map/notifications'

export const useNotificationContent = (activity: NotificationActivity, group: NotificationGroup, firstUser: any) => {
  const verb = activity.verb
  const firstActorId = activity.actor.split(':')[1]
  const isSystemActor = firstActorId === 'system' || firstActorId == 'None'
  const objectType = activity.object.split(':')[0]
  const postId = activity.data?.parentId ? activity.data.parentId : activity.object.split(':')[1]
  const { data: post } = useGetPost(postId, {
    enabled: !!postId && (objectType === 'like' || objectType === 'comment'),
  })
  const matchId = activity.foreignId.split(':')[1]
  const { data: match } = useMatchById(Number(matchId), {
    enabled: [NotificationVerb.PARTNER_MATCH_NEW_MESSAGE].includes(verb),
  })
  const isUnlocked = [NotificationVerb.PARTNER_MATCH_NEW_MESSAGE, NotificationVerb.NEW_MESSAGE].includes(verb)
    ? Boolean(match?.isCreator || match?.recipientUnlockedAt || activity.data?.messageType === 'referral')
    : true

  const getLinkId = (activity: NotificationActivity) => {
    const objectType = activity.object.split(':')[0]
    const objectId = activity.object.split(':')[1]
    if (objectType === 'like' || objectType === 'comment') {
      return activity.data?.parentId ? activity.data.parentId : objectId
    }
    if (objectType === 'referral') {
      return objectId
    }
    if (objectType === 'referral_share') {
      return objectId
    }
    if (objectType === 'message') {
      return objectId
    }
    if (objectType === 'weekly_recap') {
      return activity.data?.date
    }
    return objectId
  }

  return {
    text: getNotificationBaseContent(activity, group, firstUser, isSystemActor, isUnlocked),
    postImage: getNotificationImage(activity, post),
    avatar: NotificationMap[activity.verb].getImage(isUnlocked ? activity : undefined),
    symbol: NotificationMap[activity.verb].getSymbol(!isUnlocked),
    link: NotificationMap[activity.verb].getLink(getLinkId(activity)),
  }
}

const getNotificationBaseContent = (
  activity: NotificationActivity,
  group: NotificationGroup,
  firstUser: any,
  isSystemNotification = false,
  isUnlocked = true,
): ReactNode => {
  if (isSystemNotification) {
    return activity.data?.text || activity.verb
  }

  if (!firstUser) return ''

  const isAggregated = group.activityCount > 1
  const othersCount = group.actorCount - 1
  const userName = `${firstUser?.firstName} ${firstUser?.lastName[0]}.`
  const isMatch = activity.verb === NotificationVerb.PARTNER_MATCH_NEW_MESSAGE

  if (isMatch && !isUnlocked) {
    return 'Your match of the week sent you a message!'
  }

  if (isAggregated && othersCount > 0) {
    return `${userName} and ${othersCount} ${activity.data?.text || activity.verb}`
  }

  return `${userName} ${activity.data?.text || activity.verb}`
}

const getNotificationImage = (activity: NotificationActivity, post?: { images?: string[] }) => {
  const objectType = activity.object.split(':')[0]
  if ((objectType === 'like' || objectType === 'comment') && post?.images && post.images.length > 0) {
    return post.images[0]
  }
  return undefined
}
